import cx from 'classnames'
import { useEffect, useState } from 'react'
import { Tab } from 'react-bootstrap'
import { useDispatch } from 'react-redux'
import { useHistory, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  getCourseClassesById,
  getCourseLmsById,
  unlinkACourseToClass,
} from '@/store/knowledge/actions'
import Loading from '@/components/UI/Loading/Loading'
import ChildPageHeader from '@/components/UI/ChildPageHeader'
import CustomTabs from '@/components/UI/CustomTabs/CustomTabs'
import ClassAdmins from './ClassAdmins'
import ClassLearners from './ClassLearners'
import ClassAccessRights from './ClassAccessRights'
import ClassSettings from './ClassSettings'

const ClassDetails = () => {
  const { t } = useTranslation('translation')
  const { classId }: any = useParams()
  const history = useHistory()
  const dispatch = useDispatch()
  const [classDetail, setClassDetail] = useState(null)
  const [activeTab, setActiveTab] = useState('settings')
  const [loading, setLoading] = useState(false)
  const [courseLMS, setCourseLMS] = useState(null)

  useEffect(() => {
    funGetCourseClassesById()
    fungetCourseLmsById()
  }, [classId])

  const funGetCourseClassesById = async () => {
    try {
      setLoading(true)
      const response: any = await dispatch(getCourseClassesById(classId))
      setClassDetail(response)
    } catch (error: any) {
      history.goBack()
    } finally {
      setLoading(false)
    }
  }

  const fungetCourseLmsById = async () => {
    try {
      const response: any = await dispatch(getCourseLmsById(classId))
      setCourseLMS(response)
    } catch (error: any) {
      // LMS might not be set up, this is okay
    }
  }

  const funUnlinkACourseToClass = async () => {
    try {
      await dispatch(unlinkACourseToClass(classId))
      setCourseLMS(null)
    } catch (error: any) {
      // Handle error
    }
  }

  const tabsChangeHandler = (activeKey: string) => {
    setActiveTab(activeKey)
  }

  return (
    <>
      {loading && <Loading />}
      <section
        className={cx({
          'page-mid-wraper pb-0': true,
          'h-without-foter': true,
        })}
      >
        <div>
          <div className="d-flex flex-wrap justify-content-between">
            <ChildPageHeader
              title={classDetail?.name || t('Class Details')}
              subTitle={t(
                'Manage class admins, learners, access rights, and settings',
              )}
            />
          </div>
          <CustomTabs
            defaultActiveKey="settings"
            activeKey={activeTab}
            onTabChange={tabsChangeHandler}
            queryParamName="tab"
            validTabs={['admins', 'learners', 'access', 'settings']}
          >
            <Tab eventKey="admins" title={t('Admins')}>
              <div className="tab-content-wrapper">
                {classDetail && (
                  <ClassAdmins
                    activeKey={activeTab}
                    data={classDetail ? [...classDetail.admins] : []}
                    getClassDetails={funGetCourseClassesById}
                    className={classDetail?.name}
                  />
                )}
              </div>
            </Tab>

            <Tab eventKey="learners" title={t('Learners')}>
              <div className="tab-content-wrapper">
                {classDetail && (
                  <ClassLearners
                    activeKey={activeTab}
                    data={classDetail ? [...classDetail.students] : []}
                    getClassDetails={funGetCourseClassesById}
                    confirmHandler={funGetCourseClassesById}
                    className={classDetail?.name}
                  />
                )}
              </div>
            </Tab>

            <Tab eventKey="access" title={t('Access Rights')}>
              <div className="tab-content-wrapper">
                {classDetail && (
                  <ClassAccessRights
                    classId={classId}
                    initialAccessAllKnowledge={
                      classDetail.access_all_knowledge || false
                    }
                    initialSelectedGroupIds={
                      classDetail.knowledge_group_ids || []
                    }
                    onUpdate={funGetCourseClassesById}
                  />
                )}
              </div>
            </Tab>

            <Tab eventKey="settings" title={t('Class Settings')}>
              <div className="tab-content-wrapper py-3">
                {classDetail && (
                  <ClassSettings
                    classDetail={classDetail}
                    courseLMS={courseLMS}
                    onUpdate={funGetCourseClassesById}
                    onLMSUpdate={fungetCourseLmsById}
                    onUnlinkLMS={funUnlinkACourseToClass}
                  />
                )}
              </div>
            </Tab>
          </CustomTabs>
        </div>
      </section>
    </>
  )
}

export default ClassDetails
